"use client"

import Image from "next/image"
import { useEffect, useRef, useState } from "react"

import type { HomeCollectionSmallSlide } from "@modules/home/types"

import styles from "./collection-small.module.css"

/** Tempo di permanenza di ogni foto. */
const INTERVAL_MS = 5500

type Props = {
  slides: HomeCollectionSmallSlide[]
  title: string
}

export default function CollectionSmallSlideshow({ slides, title }: Props) {
  const count = slides.length
  const interactive = count > 1

  const rootRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const [inView, setInView] = useState(false)
  const [held, setHeld] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  // Lo slideshow gira solo mentre è visibile: niente timer fuori schermo.
  useEffect(() => {
    const node = rootRef.current

    if (!node || !interactive) {
      return
    }

    setReducedMotion(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )

    const observer = new IntersectionObserver(
      ([entry]) => setInView(Boolean(entry?.isIntersecting)),
      { threshold: 0.35 }
    )
    observer.observe(node)

    return () => observer.disconnect()
  }, [interactive])

  const playing = interactive && inView && !held && !reducedMotion

  // Il timer riparte da zero a ogni cambio foto (anche manuale) e a ogni ripresa.
  useEffect(() => {
    if (!playing) {
      return
    }

    const timeout = window.setTimeout(
      () => setActive((current) => (current + 1) % count),
      INTERVAL_MS
    )

    return () => window.clearTimeout(timeout)
  }, [playing, active, count])

  return (
    <div
      aria-label={`${title}, photo gallery`}
      aria-roledescription="carousel"
      className={styles.photo}
      onBlur={interactive ? () => setHeld(false) : undefined}
      onFocus={interactive ? () => setHeld(true) : undefined}
      onPointerEnter={interactive ? () => setHeld(true) : undefined}
      onPointerLeave={interactive ? () => setHeld(false) : undefined}
      ref={rootRef}
      role="group"
    >
      <ul className={styles.slides}>
        {slides.map((slide, index) => (
          <li
            aria-hidden={index === active ? undefined : true}
            aria-label={`${index + 1} / ${count}`}
            aria-roledescription="slide"
            className={styles.slide}
            data-active={index === active}
            key={slide.image.url}
          >
            <Image
              alt={slide.alt}
              className={styles.slideImage}
              draggable={false}
              fill
              priority={index === 0}
              quality={80}
              sizes="(min-width: 800px) 50vw, 100vw"
              src={slide.image.url}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
